import React, { useState } from 'react';
import axios from 'axios';
import { biosdkOperationUrl, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import { User, Upload, Download, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

function FaceExtractor() {
  const [file, setFile] = useState(null);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [compression, setCompression] = useState('');
  const [isoImageUrl, setIsoImageUrl] = useState(null);
  const [jp2isoImageUrl, setjp2IsoImageUrl] = useState(null);
  const [jpegImageUrl, setJpegImageUrl] = useState(null);
  const [injpegImageUrl, setInJpegImageUrl] = useState(null);
  const [timing, setTiming] = useState({
    convertInput: 0,
    convertJpeg: 0,
    convertIso: 0,
    extractionDurationMilliSeconds: 0
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    setInJpegImageUrl(null);
    let startConvertInput = 0;
    let convertInputDuration = 0;

    try {
      if (['jpeg', 'jpg', 'bmp', 'png'].includes(fileExtension)) {
        setInJpegImageUrl(URL.createObjectURL(selectedFile));
        setIsoImageUrl(null);
        setJpegImageUrl(null);
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        startConvertInput = performance.now();

        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });

        const endConvertInput = performance.now();
        convertInputDuration = endConvertInput - startConvertInput;
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        const jpegUrl = URL.createObjectURL(jpegBlob);
        setInJpegImageUrl(jpegUrl);
        setIsoImageUrl(null);
        setJpegImageUrl(null);
      } 
      else if (fileExtension === 'jp2') {
        const formData = new FormData(); 
        formData.append('file', selectedFile);  
        const isoResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },  
          responseType: 'arraybuffer',  
        });
      
        const isoBlob = new Blob([isoResponse.data], { type: 'application/octet-stream' });
        const isoUrl = URL.createObjectURL(isoBlob);
        setjp2IsoImageUrl(isoUrl); 
    
        const isoFormData = new FormData();
        isoFormData.append('file', isoBlob, 'converted-jp2.iso'); 
    
        const jpegResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },  
          responseType: 'arraybuffer',  
        });

        const jpegBlob = new Blob([jpegResponse.data], { type: 'image/jpeg' });
        const jpegUrl = URL.createObjectURL(jpegBlob);
        setInJpegImageUrl(jpegUrl);  
      } else {
        setError('Unsupported file type.');
        setFile(null);
        return;
      }
      setTiming((prevTiming) => ({
        ...prevTiming,
        convertInput: convertInputDuration,
      }));

      setIsoImageUrl(null);
      setJpegImageUrl(null);
      setShowAdvanced(false);
      setError(null);
      setSubmitted(false);
    } catch (err) {
      setError('An error occurred while processing the file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsProcessing(true); 

    if (!file || !width || !height || !compression) {
      setError('Please provide all inputs and upload a file.');
      setIsProcessing(false);
      return;
    }

    if (isNaN(width) || isNaN(height) || isNaN(compression) || width < 50 || height < 50 || compression < 0) {
      setError('Please provide valid numeric values for width, height, and compression.');
      setIsProcessing(false);
      return;
    }

    setIsoImageUrl(null);
    setJpegImageUrl(null);
    setShowAdvanced(false);
    setError(null);

    const fileExtension = file.name.split('.').pop().toLowerCase();
    let facebdbPlaceholder = [];
    let startConvertInput = 0;
    let convertInputDuration = 0;

    try {
      let isoBlob1;

      if (['jpeg', 'jpg', 'bmp', 'png', 'jp2', 'iso'].includes(fileExtension)) {
        if (fileExtension === 'jp2') {
          isoBlob1 = await fetch(jp2isoImageUrl).then(res => res.arrayBuffer());
          facebdbPlaceholder = toSignedByteJson(isoBlob1);
        } else if (fileExtension === 'iso') {
          isoBlob1 = await file.arrayBuffer();
          facebdbPlaceholder = toSignedByteJson(isoBlob1);
        } else {
          const formData = new FormData();
          formData.append('file', file);
          startConvertInput = performance.now();
          const isoResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            responseType: 'arraybuffer',
          });
          const endConvertInput = performance.now();
          convertInputDuration = endConvertInput - startConvertInput;
          isoBlob1 = isoResponse.data;
          facebdbPlaceholder = toSignedByteJson(isoBlob1);
        }
      }

      const templateResponse = await fetch('/face_extract_inner_request-template.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);

      const templateText = await templateResponse.text();
      const updatedTemplate = templateText
        .replace('<width>', width)
        .replace('<height>', height)
        .replace('<compression_level>', compression)
        .replace('facebdb', facebdbPlaceholder);

      const versionRequestTemplateResponse = await fetch('/extract_request-template.json');
      if (!versionRequestTemplateResponse.ok) throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedExtract = arrayBufferToBase64(new TextEncoder().encode(updatedTemplate));
      const finalRequestBodyExtract = versionRequestTemplateText.replace('encoded_request', base64EncodedExtract);

      const isRawInput = fileExtension !== 'iso';
      const extractUrl = biosdkOperationUrl('extract-template', isRawInput);
      logBiosdkUiRoute('FaceExtractor', isRawInput ? 'rawRoute' : 'isoRoute', extractUrl, {
        operation: 'extract-template',
      });
      const extractResponse = await axios.post(extractUrl, finalRequestBodyExtract, {
        headers: { 'Content-Type': 'application/json' },
      });

      const extractResult = extractResponse.data?.response?.response?.segments?.[0];
      const bdb = extractResult?.bdb;
      const others = extractResult?.others;
      const extractionDurationMilliSeconds = Number(others?.extractionDurationMilliSeconds) || 0;

      if (!extractResult) throw new Error('Failed to extract the BDB from the response');

      const isoBlob = new Blob([new Uint8Array(bdb)], { type: 'application/octet-stream' });
      const isoUrl = URL.createObjectURL(isoBlob);
      setIsoImageUrl(isoUrl);

      const isoFormData1 = new FormData();
      isoFormData1.append('file', isoBlob, 'extracted-image.iso');

      const startConvertOutput = performance.now();
      const outputResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData1, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'arraybuffer',
      });
      const endConvertOutput = performance.now();
      const convertOutputDuration = endConvertOutput - startConvertOutput;

      const jpegBlob = new Blob([outputResponse.data], { type: 'image/jpeg' });
      const jpegUrl = URL.createObjectURL(jpegBlob);
      setJpegImageUrl(jpegUrl);

      setTiming({
        convertInput: timing.convertInput,
        convertJpeg: convertInputDuration,
        convertIso: convertOutputDuration,
        extractionDurationMilliSeconds: extractionDurationMilliSeconds
      });

      setShowAdvanced(true);
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during the extraction process';
      setError(errorMessage);
    } finally {
      setIsProcessing(false); 
    }
  };

  const handleDownload = () => {
    if (isoImageUrl) {
      const link = document.createElement('a');
      link.href = isoImageUrl;
      link.download = 'extracted-image.iso';
      link.click();
    }
  };

  const handleReset = () => {
    setFile(null);
    setWidth('');
    setHeight('');
    setCompression('');
    setIsoImageUrl(null);
    setJpegImageUrl(null);
    setInJpegImageUrl(null);
    setError(null);
    setSubmitted(false);
    setShowAdvanced(false);
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container">
        <Card className="biochq-card face">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <User className="h-8 w-8 text-face" />
              <CardTitle className="text-3xl font-bold text-gray-800">Face Extractor</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Extract biometric templates from face images using ISO/IEC 19794-5 standard
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="file" className="text-gray-700 font-medium">
                    Upload Face Image
                  </Label>
                  <div className="upload-zone">
                    <input
                      type="file"
                      id="file"
                      accept=".png,.jpg,.jp2,.iso,.jpeg,.bmp"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, BMP, JP2, or ISO</p>
                    </label>
                  </div>
                </div>

                {file && injpegImageUrl && (
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Preview</Label>
                    <div className="preview-container">
                      <img src={injpegImageUrl} alt="Uploaded face" className="preview-image" />
                      <Badge variant="face" className="absolute top-2 right-2">Face</Badge>
                    </div>
                    <p className="text-xs text-gray-500 text-center">{file.name}</p>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="width" className="text-gray-700 font-medium text-sm">Width (px)</Label>
                    <Input
                      type="number"
                      id="width"
                      placeholder="Width"
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      min="50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="height" className="text-gray-700 font-medium text-sm">Height (px)</Label>
                    <Input
                      type="number"
                      id="height"
                      placeholder="Height"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      min="50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="compression" className="text-gray-700 font-medium text-sm">Compression</Label>
                    <Input
                      type="number"
                      id="compression"
                      placeholder="Level"
                      value={compression}
                      onChange={(e) => setCompression(e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmit}
                    disabled={!file || !width || !height || !compression || isProcessing}
                    variant="face"
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <User className="mr-2 h-4 w-4" />
                        Extract Template
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline" disabled={isProcessing}>
                    Reset
                  </Button>
                </div>
              </div>

              {/* Output Section */}
              <div className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {isoImageUrl && !error && (
                  <>
                    <Alert variant="success">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertTitle>Extraction Complete</AlertTitle>
                      <AlertDescription>Face template extracted successfully</AlertDescription>
                    </Alert>

                    {jpegImageUrl && (
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-medium">Extracted Result</Label>
                        <div className="preview-container">
                          <img src={jpegImageUrl} alt="Extracted face" className="preview-image" />
                        </div>
                      </div>
                    )}

                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Extraction Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Output Dimensions:</span>
                          <span className="font-medium">{width} x {height} px</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Compression Level:</span>
                          <span className="font-medium">{compression}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Template Format:</span>
                          <span className="font-medium text-xs">ISO/IEC 19794-5</span>
                        </div>
                      </CardContent>
                    </Card>

                    {showAdvanced && (
                      <Card className="timing-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Timing Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Display Conversion:</span>
                            <span className="font-medium">{timing.convertInput.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">ISO Conversion:</span>
                            <span className="font-medium">{timing.convertJpeg.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Extraction Time:</span>
                            <span className="font-medium text-primary">{timing.extractionDurationMilliSeconds.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Output Conversion:</span>
                            <span className="font-medium">{timing.convertIso.toFixed(2)} ms</span>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Button onClick={handleDownload} variant="outline" className="w-full">
                      <Download className="mr-2 h-4 w-4" />
                      Download ISO Template
                    </Button>
                  </>
                )}

                {!isoImageUrl && !error && submitted && (
                  <div className="flex items-center justify-center h-48 text-gray-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}

                {!isoImageUrl && !error && !submitted && (
                  <div className="empty-state">
                    <User className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-sm">Upload a face image and click Extract</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default FaceExtractor;
